import ThemeName from "@/types/styles/ThemeName";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowDownShortWide, faArrowDownWideShort, faAtom, faBan, faCalendarDays, faCancel, faCheck, faCheckCircle, faCheckSquare, faChevronDown, faChevronUp, faClipboardList, faClockRotateLeft, faCodeBranch, faCommentAltLines, faDash, faDatabase, faDownload, faExclamationTriangle, faFileChartColumn, faFileCheck, faFileSearch, faFilters, faFlag, faForwardFast, faGears, faHospital, faInfoCircle, faInputText, faLink, faListOl, faLock, faLockA, faObjectGroup, faPencil, faPeopleGroup, faQuestion, faQuestionCircle, faRefresh, faRepeat, faSave, faSearch, faSitemap, faSquare, faSquareList, faTable, faTableList, faText, faTimeline, faTimer, faTimes, faTimesHexagon, faTrashAlt, faUnlink, faUser, faUserGroup } from "@fortawesome/pro-duotone-svg-icons";
import { faEllipsisV, faPlus } from "@fortawesome/pro-solid-svg-icons";

export interface IPaletteSettings {
    primaryLight: string;
    secondaryLight: string;
    primaryDark: string;
    secondaryDark: string;
};

export interface IClientSettings {
    eventStreamUrl?: string;
    eventStreamDebug?: boolean;
    baseUrl?: string;
    basePath?: string;
};

export interface ISentrySettings {
    dsn?: string;
    application: string;
    release: string;
    environment: string;
    customer: string;
    sendDefaultPii: boolean;
    enableMetrics: boolean;
    attachStacktrace: boolean;
    debug: boolean;
    enableLogs: boolean;
    enableReportABug: boolean;
    replaysOnErrorSampleRate: number;
    replaysSessionSampleRate: number;
    tracesSampleRate: number;
};

interface IClientAppSettingsPayload {
    settings?: {
        sentry?: Partial<ISentrySettings>;
    };
    Settings?: {
        Sentry?: Partial<ISentrySettings>;
    };
}

const injectedAppSettings = (window.APP_SETTINGS ?? {}) as IClientAppSettingsPayload;
const injectedSentrySettings = injectedAppSettings.settings?.sentry ?? injectedAppSettings.Settings?.Sentry;

const parseBoolean = (value: string | undefined, fallback: boolean): boolean => {
    if (value === undefined) {
        return fallback;
    }

    return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
};

const parseNumber = (value: string | undefined, fallback: number): number => {
    const parsed = value === undefined ? Number.NaN : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const serverOrEnvironmentBoolean = (serverValue: boolean | undefined, environmentValue: string | undefined, fallback: boolean) =>
    serverValue ?? parseBoolean(environmentValue, fallback);

const serverOrEnvironmentNumber = (serverValue: number | undefined, environmentValue: string | undefined, fallback: number) =>
    serverValue ?? parseNumber(environmentValue, fallback);

export interface IChannelSettings {
    updateStatus: string;
    trial: string;
    masterGroup: string;
    coordinatingGroup: string;
    institution: string;
    participant: string;

    caseReportFormState: string;
    review: string;
    verification: string;

    note: string;
    finding: string;
};

export interface ICtcaeSettings {
    specificationItemName: string;
    termItemName: string;
    termId: string;
};

export interface IMetadataSettings {
    studyEventNullKey: string;
    itemGroupUngroupedKey: string;
};

export interface IIconSettings {
    trial: IconProp;
    masterGroup: IconProp;
    coordinatingGroup: IconProp;
    institution: IconProp;
    participant: IconProp;

    caseReportForm: IconProp;
    review: IconProp;
    verification: IconProp;
    combined: IconProp;
    note: IconProp;
    audit: IconProp;
    finding: IconProp;
    report: IconProp;
    configuration: IconProp;
    sourceData: IconProp;
    utilities: IconProp;

    event: IconProp;
    form: IconProp;
    formVersion: IconProp;
    itemGroup: IconProp;
    itemGrid: IconProp;
    item: IconProp;
    itemValue: IconProp;

    ascending: IconProp;
    descending: IconProp;
    add: IconProp;
    delete: IconProp;
    refresh: IconProp;
    close: IconProp;
    locked: IconProp;
    disabled: IconProp;
    notApplicable: IconProp;
    search: IconProp;
    filter: IconProp;
    help: IconProp;
    save: IconProp;
    cancel: IconProp;

    link: IconProp;
    unlink: IconProp;
    download: IconProp;

    alertSuccess: IconProp;
    alertInformation: IconProp;
    alertWarning: IconProp;
    alertError: IconProp;
    alertDefault: IconProp;

    priorityInherit: IconProp;
    priorityLow: IconProp;
    priorityNormal: IconProp;
    priorityHigh: IconProp;
    priorityCritical: IconProp;

    statusNone: IconProp;
    statusSkipped: IconProp;
    statusLocked: IconProp;
    statusStarted: IconProp;
    statusComplete: IconProp;

    itemValueCheckboxUnchecked: IconProp;
    itemValueCheckboxChecked: IconProp;
    itemValueDate: IconProp;
    itemValueText: IconProp;

    studyEventRepeat: IconProp;
    itemGroupRepeat: IconProp;

    user: IconProp;

    auditInsert: IconProp;
    auditUpdate: IconProp;
    auditDelete: IconProp;
    auditUnknown: IconProp;

    auditTable: IconProp;
    auditTimeline: IconProp;
};

export interface IAppSettings {
    overrideTheme?: ThemeName;
    client?: IClientSettings;
    sentry: ISentrySettings;
    palette: IPaletteSettings;
    icons: IIconSettings;
    channels: IChannelSettings;
    debug?: boolean;
    allowNotesAndQueriesEdit?: boolean;
    metadata: IMetadataSettings;
    ctcae: ICtcaeSettings;
    trialPortalUrl: string;
};

export const AppSettings: IAppSettings = {
    //overrideTheme: 'light',
    sentry: {
        dsn: injectedSentrySettings?.dsn ?? import.meta.env.VITE_SENTRY_DSN,
        application: import.meta.env.VITE_SENTRY_APPLICATION ?? 'TissueBank',
        release: import.meta.env.VITE_SENTRY_RELEASE ?? 'bct-tissuebank-frontend@1.0.0',
        environment: injectedSentrySettings?.environment ?? import.meta.env.VITE_SENTRY_ENVIRONMENT ?? (import.meta.env.DEV ? 'Development' : 'Production'),
        customer: injectedSentrySettings?.customer ?? import.meta.env.VITE_SENTRY_CUSTOMER ?? 'BCT',
        sendDefaultPii: serverOrEnvironmentBoolean(injectedSentrySettings?.sendDefaultPii, import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII, false),
        enableMetrics: serverOrEnvironmentBoolean(injectedSentrySettings?.enableMetrics, import.meta.env.VITE_SENTRY_ENABLE_METRICS, false),
        attachStacktrace: serverOrEnvironmentBoolean(injectedSentrySettings?.attachStacktrace, import.meta.env.VITE_SENTRY_ATTACH_STACKTRACE, false),
        debug: serverOrEnvironmentBoolean(injectedSentrySettings?.debug, import.meta.env.VITE_SENTRY_DEBUG, false),
        enableLogs: serverOrEnvironmentBoolean(injectedSentrySettings?.enableLogs, import.meta.env.VITE_SENTRY_ENABLE_LOGS, false),
        enableReportABug: serverOrEnvironmentBoolean(injectedSentrySettings?.enableReportABug, import.meta.env.VITE_SENTRY_ENABLE_REPORT_A_BUG, false),
        replaysOnErrorSampleRate: serverOrEnvironmentNumber(injectedSentrySettings?.replaysOnErrorSampleRate, import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE, 1),
        replaysSessionSampleRate: serverOrEnvironmentNumber(injectedSentrySettings?.replaysSessionSampleRate, import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE, 0.1),
        tracesSampleRate: serverOrEnvironmentNumber(injectedSentrySettings?.tracesSampleRate, import.meta.env.VITE_SENTRY_TRACE_SAMPLE_RATE, 0.2),
    },
    client: {
        eventStreamUrl: import.meta.env.VITE_EVENT_STREAM_URL,
        baseUrl: import.meta.env.VITE_API_URL,
        basePath: '/api',
        eventStreamDebug: import.meta.env.DEV
    },
    palette: {
        primaryDark: '#7d6699',
        primaryLight: '#562c85',
        secondaryDark: '#99567e',
        secondaryLight: '#e8078c'
    },
    icons: {
        trial: faAtom,
        masterGroup: faPeopleGroup,
        coordinatingGroup: faUserGroup,
        institution: faHospital,
        participant: faUser,


        caseReportForm: faClipboardList,
        verification: faFileCheck,
        review: faFileSearch,
        combined: faEllipsisV,
        note: faFlag,
        audit: faClockRotateLeft,
        finding: faCommentAltLines,
        report: faFileChartColumn,
        configuration: faGears,
        sourceData: faDatabase,
        utilities: faGears,

        event: faCalendarDays,
        form: faSquareList,
        formVersion: faCodeBranch,
        itemGroup: faObjectGroup,
        itemGrid: faTableList,
        item: faInputText,
        itemValue: faListOl,


        ascending: faArrowDownShortWide,
        descending: faArrowDownWideShort,
        add: faPlus,
        delete: faTrashAlt,
        refresh: faRefresh,
        close: faTimes,
        locked: faLock,
        disabled: faBan,
        notApplicable: faDash,
        search: faSearch,
        filter: faFilters,
        help: faQuestionCircle,
        save: faSave,
        cancel: faCancel,

        link: faLink,
        unlink: faUnlink,
        download: faDownload,

        alertSuccess: faCheckCircle,
        alertInformation: faInfoCircle,
        alertWarning: faExclamationTriangle,
        alertError: faTimesHexagon,
        alertDefault: faBan,

        priorityInherit: faSitemap,
        priorityLow: faChevronDown,
        priorityNormal: faDash,
        priorityHigh: faChevronUp,
        priorityCritical: faExclamationTriangle,

        statusNone: faTimes,
        statusSkipped: faForwardFast,
        statusLocked: faLockA,
        statusStarted: faTimer,
        statusComplete: faCheck,

        
        itemValueCheckboxUnchecked: faSquare,
        itemValueCheckboxChecked: faCheckSquare,
        itemValueDate: faCalendarDays,
        itemValueText: faText,

        studyEventRepeat: faRepeat,
        itemGroupRepeat: faRepeat,

        user: faUser,

        auditInsert: faPlus,
        auditUpdate: faPencil,
        auditDelete: faTrashAlt,
        auditUnknown: faQuestion,

        auditTable: faTable,
        auditTimeline: faTimeline 
    },
    channels: {
        updateStatus: 'status',
        trial: 'trial',
        masterGroup: 'master-group',
        coordinatingGroup: 'coordinating-group',
        institution: 'institution',
        participant: 'participant',

        caseReportFormState: 'case-report-form-state',
        review: 'review',
        verification: 'verification',

        note: 'note',
        finding: 'finding'
    },
    debug: import.meta.env.DEV,
    allowNotesAndQueriesEdit: true,
    metadata: {
        itemGroupUngroupedKey: 'UNGROUPED',
        studyEventNullKey: 'NULL_KEY'
    },
    ctcae: {
        specificationItemName: 'CtcaeSpecificationItemName',
        termItemName: 'CtcaeTermItemName',
        termId: 'CtcaeTermId'
    },
    trialPortalUrl: (window as any).TRIAL_PORTAL_URL ?? import.meta.env.VITE_TRIALPORTAL_BASE_URL
};
